import { createRouter, createWebHistory, START_LOCATION } from "vue-router"
import { IS_VIEW_HELP_EMBED, documentLoadUrl } from "@/services/viewHelp/embed"
import settings from "@/router/routes/settings"
import category from "@/router/routes/category"
import employee from "@/router/routes/employee"
import project from "@/router/routes/project"
import machine from "@/router/routes/machine"
import material from "@/router/routes/material"
import additionaleffort from "@/router/routes/additionaleffort"
import customer from "@/router/routes/customer"
import auth from "@/router/routes/auth"
import dashboard from "@/router/routes/dashboard"
import userposition from "@/router/routes/userposition"
import packet from "@/router/routes/packet"
import hourlywageinvoice from "@/router/routes/hourlywageinvoice"
import personnelplanning from "@/router/routes/personnelplanning"
import timetracking from "@/router/routes/timetracking"
import daysubmission from "@/router/routes/daysubmission"
import audit from "@/router/routes/audit"
import billing from "@/router/routes/billing"
import notification from "@/router/routes/notification"
import releasenotes from "@/router/routes/releasenotes"
import file from "@/router/routes/file"
import { initializeNavigationGuards } from "./navigation-guard"
import { initializeNewTabIntent } from "./newTabIntent"
import { isUpdatePending } from "@/services/versionCheck"
import { initializeUnsavedChangesGuard } from "@/services/unsavedChanges"
import { auth as session } from "@/services/auth"

const routes = [
  ...auth,
  ...settings,
  ...dashboard,
  ...employee,
  ...category,
  ...material,
  ...machine,
  ...additionaleffort,
  ...project,
  ...customer,
  ...userposition,
  ...packet,
  ...hourlywageinvoice,
  ...personnelplanning,
  ...timetracking,
  ...daysubmission,
  ...audit,
  ...billing,
  ...notification,
  ...releasenotes,
  ...file,
  // This is a Vue-Rouer Caveat for using `history` mode.
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/error/NotFoundPage"),
    meta: { title: "general.not_found_page" },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// After a new production deployment, Vite generates new content-hashed filenames for all
// lazy-loaded route chunks. Users who still have an old tab open will try to fetch chunk
// URLs that no longer exist on the server, causing a "Failed to fetch dynamically imported
// module" error. When this happens, we force a hard reload to the intended route so the
// browser fetches the fresh index.html and resolves the correct new chunk filenames.
// These same messages are filtered from Sentry (see plugins/sentry.js) since the reload
// already resolves them for the user.
const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS for/
// Session key remembering which target we already force-reloaded, so a chunk that is still
// unreachable after the reload (stale CDN edge, blocking extension, genuinely broken deploy)
// cannot trap the user in an endless reload loop. Storage access is wrapped because it can
// throw in private modes / embedded webviews, and afterEach runs on every navigation.
const RELOAD_GUARD_KEY = "chunkReloadTarget"
const readReloadGuard = () => {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY)
  } catch {
    return null
  }
}
const writeReloadGuard = value => {
  try {
    if (value === null) sessionStorage.removeItem(RELOAD_GUARD_KEY)
    else sessionStorage.setItem(RELOAD_GUARD_KEY, value)
  } catch {
    /* storage unavailable — fall back to unguarded reload behaviour */
  }
}

router.onError((error, to) => {
  if (!CHUNK_LOAD_ERROR.test(error?.message ?? "")) return
  // Never inside the Erklärung's frame. Its `sessionStorage` is a fresh in-memory copy on every load
  // (services/viewHelp/isolation.ts), so the guard below would be forgotten with each reload and a chunk
  // that stays unreachable would reload the frame forever. The walkthrough has its own way back: a step
  // whose view does not arrive has the frame reloaded by the player, once.
  if (IS_VIEW_HELP_EMBED) return
  // Only reload once per target; if we already tried, let the error surface instead of looping.
  if (readReloadGuard() === to.fullPath) return
  writeReloadGuard(to.fullPath)
  window.location.assign(to.fullPath)
})

// A successful navigation means chunks are loading fine again — clear the guard so a later
// deployment in the same session can trigger a fresh reload.
router.afterEach(() => writeReloadGuard(null))

/**
 * Swaps a pending release in during the next page change, so most users never see the update
 * prompt at all: leaving a page discards that page's state anyway, which makes it the one moment
 * where a full document load costs the user nothing.
 *
 * Registered last, after the auth/subscription guards, so that a redirect is settled before we
 * commit to leaving the SPA — the guard simply runs again for the redirect target.
 */
const initializeUpdateGuard = router => {
  router.beforeEach((to, from) => {
    if (!isUpdatePending()) return true
    // Nothing has been rendered yet on the very first navigation; a full load here would mean
    // loading the app twice in a row.
    if (from === START_LOCATION) return true
    // Query- and hash-only changes carry in-page state — a selected tab, an open dialog, a filter.
    // Reloading on those would throw away what the user is in the middle of, so wait for a move to
    // a genuinely different page.
    if (to.path === from.path) return true

    // A document load, not a route change: this is what fetches the revalidated index.html and
    // with it the new asset URLs. Kept inside the Erklärung's frame when it happens there.
    window.location.assign(documentLoadUrl(to.fullPath))
    return false
  })
}

export default router

initializeNavigationGuards(router)
// Before the update guard: the release swap is a document load, and that may only happen once the
// page with its unsaved edits has been let go.
initializeUnsavedChangesGuard(router, () => Boolean(session.isLoggedIn()))
initializeUpdateGuard(router)
initializeNewTabIntent(router)
